import { useState } from "react"
import PropTypes from "prop-types"
import { FilterList, PivotTableChart, ViewColumn } from "@mui/icons-material"
import { Alert, Divider, Tooltip } from "@mui/material"
import { ColumnsPanelTrigger, DataGridPremium as DataGrid, FilterPanelTrigger, GridColumnMenu, PivotPanelTrigger, Toolbar, ToolbarButton, useGridApiRef } from "@mui/x-data-grid-premium"
import { ErrorBoundary } from "@sentry/react"
import { MAX_PIVOT_COLUMNS } from "helpers/ai"
import FullSizeArtifactDialog from "../../full-size-artifact-dialog"
import ExplainSteps from "./explain-steps"
import ExportMenu from "./toolbar-components/export-menu"
import QuickFilter from "./toolbar-components/quick-filter"

export default function FullSizeTable({ open, onClose, columns, rows, explainSteps }) {
  const apiRef = useGridApiRef()
  const [pivotWarning, setPivotWarning] = useState(null)

  const CustomColumnMenu = (props) => {
    return (
      <GridColumnMenu
        {...props}
        slots={{
          columnMenuAggregationItem: null,
          columnMenuGroupingItem: null,
        }}
      />
    )
  }

  const CustomToolbar = (props) => {
    return <Toolbar {...props}>
      <Tooltip title="Columns">
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumn fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>
      <Tooltip title="Filters">
        <FilterPanelTrigger render={<ToolbarButton />}>
          <FilterList fontSize="small" />
        </FilterPanelTrigger>
      </Tooltip>
      <Tooltip title="Pivot">
        <PivotPanelTrigger render={<ToolbarButton />}>
          <PivotTableChart fontSize="small" />
        </PivotPanelTrigger>
      </Tooltip>
      <Divider orientation="vertical" sx={{ height: "50%" }} />
      <ExportMenu />
      <Divider orientation="vertical" sx={{ height: "50%" }} />
      <QuickFilter />
    </Toolbar>
  }

  const handlePivotModelChange = (model) => {
    const pivotColumns = model?.columns || []
    if (pivotColumns.length > MAX_PIVOT_COLUMNS) {
      setPivotWarning(`Too many pivot columns selected. Maximum allowed is ${MAX_PIVOT_COLUMNS}.`)
      // Reset to previous state
      apiRef.current.setPivotModel({
        ...model,
        columns: pivotColumns.slice(0, MAX_PIVOT_COLUMNS),
      })
    } else {
      setPivotWarning(null)
    }
  }

  return <FullSizeArtifactDialog
    open={open}
    onClose={onClose}
    title="Generated Table"
  >
    {explainSteps && <ExplainSteps explainSteps={explainSteps} />}
    {pivotWarning && (
      <Alert severity="warning" className="pivot-warning">
        {pivotWarning}
      </Alert>
    )}
    <ErrorBoundary fallback={<div className="grid-error-boundary">⚠️ Failed to load the grid.</div>}>
      <DataGrid
        ignoreValueFormatterDuringExport
        className="table-artifact-full-screen-data-grid"
        apiRef={apiRef}
        density="compact"
        rows={rows}
        columns={columns}
        onPivotModelChange={handlePivotModelChange}
        slots={{
          columnMenu: CustomColumnMenu,
          toolbar: CustomToolbar,
        }}
        showToolbar
        pagination
      />
    </ErrorBoundary>
  </FullSizeArtifactDialog>
}

FullSizeTable.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  explainSteps: PropTypes.string,
}
